import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  selectPrimary,
  resolveCompanyId,
  type CompanyMembership,
} from '@/lib/memberships-utils';

// user_company_memberships isn't in the generated Supabase types yet
// (the types file is regenerated separately). Cast through any matches
// the convention in useWeeklySales / useWeatherData / useLiveSalesData.
const db = supabase as any;

export type { CompanyMembership } from '@/lib/memberships-utils';

/**
 * Reads the current user's company memberships from
 * `user_company_memberships`. RLS scopes the result to the caller, so
 * no explicit user filter is required server-side, but we key the
 * query by user_id to invalidate when the signed-in user changes.
 *
 * Phase A.1 PR 2: published but no consumers yet. LocationContext
 * continues to read `profiles.company_id` directly. PR 3 will switch
 * LocationContext to consume `resolvedCompanyId` from this hook, with
 * `profile.company_id` as a fallback while RLS policies are migrated
 * table-by-table.
 */
export function useCompanyMemberships() {
  const { user, profile } = useAuth();
  const userId = user?.id ?? profile?.user_id ?? null;

  const query = useQuery<CompanyMembership[]>({
    queryKey: ['company-memberships', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await db
        .from('user_company_memberships')
        .select('id,user_id,company_id,role,is_primary,created_at,updated_at')
        .order('is_primary', { ascending: false })
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CompanyMembership[];
    },
    staleTime: 1000 * 60 * 5, // memberships change on rare admin actions; 5-min cache
  });

  const memberships = useMemo(() => query.data ?? [], [query.data]);
  const primaryMembership = useMemo(() => selectPrimary(memberships), [memberships]);
  const companyIds = useMemo(() => memberships.map((m) => m.company_id), [memberships]);
  const resolvedCompanyId = useMemo(
    () => resolveCompanyId(memberships, profile?.company_id),
    [memberships, profile?.company_id],
  );

  return {
    memberships,
    primaryMembership,
    companyIds,
    resolvedCompanyId,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
